import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import logo from "@/assets/logo-small.png";
import { Coins, LogOut, Moon, Sun, Menu, X, AlertTriangle } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { motion } from "framer-motion";
import { useTheme } from "next-themes";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/contexts/AuthContext";
import { fmtTR } from "@/lib/utils";
import { LowCreditWarning } from "@/components/LowCreditWarning";
import { NotificationBell } from "@/components/NotificationBell";
import { NotificationToggle } from "@/components/NotificationToggle";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface NavbarProps {
  user?: User | null;
  showAuthButtons?: boolean;
}

export const Navbar = ({ user: propUser, showAuthButtons = true }: NavbarProps) => {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const isMobile = useIsMobile();
  const { avatarUrl } = useAuth();
  const [user, setUser] = useState<User | null>(propUser ?? null);
  const [credits, setCredits] = useState<number>(0);
  const [prevCredits, setPrevCredits] = useState<number>(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (propUser !== undefined) return;
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, [propUser]);

  useEffect(() => {
    if (user) {
      fetchUserCredits();
      checkAdminRole();
    }
  }, [user]);

  const fetchUserCredits = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_credits')
        .select('credits')
        .eq('user_id', user.id)
        .single();

      if (!error && data) {
        setPrevCredits(credits);
        setCredits(data.credits);
      }
    } catch (error) {
      console.error('Error fetching credits:', error);
    }
  };

  const checkAdminRole = async () => {
    if (!user) return;
    
    try {
      // Use the has_role database function for reliable admin check
      const { data, error } = await supabase
        .rpc('has_role', { _user_id: user.id, _role: 'admin' });

      if (error) {
        console.error('Error checking admin role via RPC:', error);
        // Fallback to direct query
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'admin')
          .maybeSingle();
        
        if (!roleError && roleData) {
          setIsAdmin(true);
        }
        return;
      }

      setIsAdmin(data === true);
    } catch (error) {
      console.error('Error checking admin role:', error);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-b border-border/40 shadow-sm theme-transition">
      <div className="container mx-auto px-3 sm:px-4 py-2.5 sm:py-3 md:py-4 theme-transition">
        <div className="flex items-center justify-between gap-2">
          <button 
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 sm:gap-2 hover:opacity-80 transition-opacity flex-shrink-0"
          >
            <img 
              src={logo} 
              alt="UGC Video Logo" 
              width={44}
              height={44}
              className="h-8 w-8 sm:h-9 sm:w-9 md:h-11 md:w-11 flex-shrink-0 rounded-lg shadow-sm transition-all duration-300 hover:scale-110 hover:brightness-110 hover:shadow-lg" 
              fetchPriority="high"
            />
            <span className="text-sm sm:text-base md:text-xl font-bold gradient-text whitespace-nowrap">
              UGC Video
            </span>
          </button>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center gap-1">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/")}
              className="text-foreground hover:text-primary text-sm px-3"
            >
              Ana Sayfa
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => navigate("/about")}
              className="text-foreground hover:text-primary text-sm px-3"
            >
              Hakkımızda
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/vitrin")}
              className="text-foreground hover:text-primary text-sm px-3"
            >
              Vitrin
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/blog")}
              className="text-foreground hover:text-primary text-sm px-3"
            >
              Blog
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => navigate("/faq")}
              className="text-foreground hover:text-primary text-sm px-3"
            >
              SSS
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => navigate("/contact")}
              className="text-foreground hover:text-primary text-sm px-3"
            >
              İletişim
            </Button>
          </div>
          
          {showAuthButtons && (
            <div className="flex items-center gap-1 sm:gap-2 md:gap-3 flex-shrink-0">
              {/* Mobil: giriş yapmamış kullanıcı için görünür Giriş Yap butonu
                  (hamburger menünün içinde kaybolmasın — kullanıcı kolayca bulsun) */}
              {!user && (
                <Button
                  variant="gradient"
                  size="sm"
                  onClick={() => navigate("/auth?mode=login")}
                  className="md:hidden text-xs px-3 flex-shrink-0"
                >
                  Giriş Yap
                </Button>
              )}
              {/* Mobile Hamburger Menu */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Menü aç">
                      <Menu className="h-5 w-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[280px] sm:w-[320px] overflow-y-auto">
                    <SheetHeader>
                      <SheetTitle className="flex items-center gap-2">
                        <img src={logo} alt="UGC Video" width={36} height={36} className="h-9 w-9 rounded-lg shadow-sm transition-all duration-300 hover:scale-110 hover:brightness-110" />
                        <span className="gradient-text">UGC Video</span>
                      </SheetTitle>
                    </SheetHeader>
                    <div className="flex flex-col gap-4 mt-8 pb-8">
                      {/* Navigation Links */}
                      <div className="space-y-1 border-b pb-4">
                        <Button
                          variant="ghost"
                          onClick={() => {
                            navigate("/");
                            setMobileMenuOpen(false);
                          }}
                          className="w-full justify-start"
                        >
                          Ana Sayfa
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            navigate("/about");
                            setMobileMenuOpen(false);
                          }}
                          className="w-full justify-start"
                        >
                          Hakkımızda
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            navigate("/vitrin");
                            setMobileMenuOpen(false);
                          }}
                          className="w-full justify-start"
                        >
                          Vitrin
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            navigate("/blog");
                            setMobileMenuOpen(false);
                          }}
                          className="w-full justify-start"
                        >
                          Blog
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            navigate("/faq");
                            setMobileMenuOpen(false);
                          }}
                          className="w-full justify-start"
                        >
                          SSS
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            navigate("/contact");
                            setMobileMenuOpen(false);
                          }}
                          className="w-full justify-start"
                        >
                          İletişim
                        </Button>
                        {!user && (
                          <Button
                            variant="ghost"
                            onClick={() => {
                              navigate("/pricing");
                              setMobileMenuOpen(false);
                            }}
                            className="w-full justify-start"
                          >
                            Fiyatlandırma
                          </Button>
                        )}
                      </div>
                      
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setTheme(theme === "dark" ? "light" : "dark");
                        }}
                        className="justify-start gap-2"
                      >
                        {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                        {theme === "dark" ? "Açık Tema" : "Koyu Tema"}
                      </Button>
                      
                      {user ? (
                        <>
                          <div className={`flex items-center gap-3 p-3 rounded-lg ${
                            credits < 40 
                              ? 'bg-red-500/10 border border-red-500/30' 
                              : credits < 80 
                                ? 'bg-amber-500/10 border border-amber-500/30'
                                : 'bg-muted'
                          }`}>
                            <Avatar className="h-10 w-10">
                              <AvatarImage src={avatarUrl} alt={user.email || ""} />
                              <AvatarFallback className="bg-gradient-primary text-white">
                                {user.email?.charAt(0).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{user.email}</p>
                              <div className={`flex items-center gap-1 text-sm ${
                                credits < 40 
                                  ? 'text-red-600 dark:text-red-400 font-medium' 
                                  : credits < 80 
                                    ? 'text-amber-600 dark:text-amber-400 font-medium'
                                    : 'text-muted-foreground'
                              }`}>
                                {credits < 40 ? (
                                  <AlertTriangle className="h-3 w-3" />
                                ) : (
                                  <Coins className="h-3 w-3" />
                                )}
                                <span>{fmtTR(credits)} Kredi</span>
                                {credits < 80 && (
                                  <span className="text-xs opacity-70">
                                    ({Math.floor(credits / 40)} video)
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {credits < 80 && (
                            <div className={`p-3 rounded-lg text-sm ${
                              credits < 40 
                                ? 'bg-red-500/10 text-red-700 dark:text-red-300 border border-red-500/30' 
                                : 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30'
                            }`}>
                              {credits < 40 ? (
                                <>⚠️ Video oluşturmak için yeterli krediniz yok!</>
                              ) : (
                                <>⚡ Düşük kredi uyarısı - sadece {Math.floor(credits / 40)} video kaldı</>
                              )}
                            </div>
                          )}
                          
                          <Button
                            variant="outline"
                            onClick={() => {
                              navigate("/pricing");
                              setMobileMenuOpen(false);
                            }}
                            className="justify-start gap-2"
                          >
                            <Coins className="h-4 w-4" />
                            Kredi Al
                          </Button>
                          
                          <div className="border-t pt-4 mt-2 space-y-2">
                            <Button
                              variant="ghost"
                              onClick={() => {
                                navigate("/profil");
                                setMobileMenuOpen(false);
                              }}
                              className="w-full justify-start"
                            >
                              Hesabım
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => {
                                navigate("/results");
                                setMobileMenuOpen(false);
                              }}
                              className="w-full justify-start"
                            >
                              Stüdyom
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => {
                                navigate("/hikaye-studyosu");
                                setMobileMenuOpen(false);
                              }}
                              className="w-full justify-start"
                            >
                              Hikaye Stüdyosu
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => {
                                navigate("/influencers");
                                setMobileMenuOpen(false);
                              }}
                              className="w-full justify-start"
                            >
                              🧑‍🎤 Influencer'larım
                            </Button>

                            <Button
                              variant="ghost"
                              onClick={() => {
                                navigate("/davet");
                                setMobileMenuOpen(false);
                              }}
                              className="w-full justify-start"
                            >
                              🎁 Davet Et & Kazan
                            </Button>

                            <Button
                              variant="ghost"
                              onClick={() => {
                                navigate("/analytics");
                                setMobileMenuOpen(false);
                              }}
                              className="w-full justify-start"
                            >
                              Video Analizleri
                            </Button>
                            
                            <Button
                              variant="ghost"
                              onClick={() => {
                                navigate("/payment-history");
                                setMobileMenuOpen(false);
                              }}
                              className="w-full justify-start"
                            >
                              Ödeme Geçmişi
                            </Button>
                            
                            <Button
                              variant="ghost"
                              onClick={() => {
                                navigate("/integrations");
                                setMobileMenuOpen(false);
                              }}
                              className="w-full justify-start"
                            >
                              Entegrasyonlar
                            </Button>
                            
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                onClick={() => {
                                  navigate("/admin");
                                  setMobileMenuOpen(false);
                                }}
                                className="w-full justify-start text-primary"
                              >
                                🛡️ Admin Paneli
                              </Button>
                            )}
                            
                            <NotificationToggle variant="button" />

                            <Button
                              variant="ghost"
                              onClick={async () => {
                                await supabase.auth.signOut();
                                setUser(null);
                                setMobileMenuOpen(false);
                                navigate("/");
                                window.location.reload();
                              }}
                              className="w-full justify-start text-destructive hover:text-destructive"
                            >
                              <LogOut className="mr-2 h-4 w-4" />
                              Çıkış Yap
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div className="space-y-3 pt-4">
                          <Button
                            variant="outline"
                            onClick={() => {
                              navigate("/auth?mode=login");
                              setMobileMenuOpen(false);
                            }}
                            className="w-full"
                          >
                            Giriş Yap
                          </Button>
                          <Button
                            variant="gradient"
                            onClick={() => {
                              navigate("/auth?mode=signup");
                              setMobileMenuOpen(false);
                            }}
                            className="w-full"
                          >
                            Kayıt Ol
                          </Button>
                        </div>
                      )}
                    </div>
                  </SheetContent>
                </Sheet>
              
              {/* 🌐 Dil seçici — tüm site seçilen dile çevrilir (DE/EN öncelikli) */}
              <LanguageSwitcher compact />

              {/* Desktop Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                className="h-8 w-8 sm:h-9 sm:w-9 relative overflow-hidden group hidden md:flex flex-shrink-0"
              >
                <div className="relative w-full h-full flex items-center justify-center">
                  <Sun className="h-3.5 w-3.5 sm:h-4 sm:w-4 absolute rotate-0 scale-100 transition-all duration-500 ease-in-out dark:-rotate-90 dark:scale-0 group-hover:text-primary" />
                  <Moon className="h-3.5 w-3.5 sm:h-4 sm:w-4 absolute rotate-90 scale-0 transition-all duration-500 ease-in-out dark:rotate-0 dark:scale-100 group-hover:text-primary" />
                </div>
                <span className="sr-only">Tema değiştir</span>
              </Button>
              
              {/* Desktop Pricing Button — sadece giriş yapmamışlara (girişte 'Kredi Al' var) */}
              {!user && (
                <Button
                  variant="ghost"
                  onClick={() => navigate("/pricing")}
                  className="text-foreground hover:text-accent-foreground text-xs sm:text-sm md:text-base px-2 sm:px-3 md:px-4 hidden md:flex"
                >
                  Fiyatlandırma
                </Button>
              )}
              
              {/* Desktop User Section */}
              {user ? (
                <>
                  {/* Low Credit Warning Popup */}
                  <LowCreditWarning currentCredits={credits} threshold={80} />
                  
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <motion.div 
                          className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer ${
                            credits < 40 
                              ? 'bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400' 
                              : credits < 80 
                                ? 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400'
                                : 'bg-muted border-border'
                          }`}
                          animate={prevCredits !== credits && credits < prevCredits ? {
                            scale: [1, 1.2, 0.95, 1],
                            backgroundColor: ["hsl(var(--muted))", "hsl(var(--destructive) / 0.2)", "hsl(var(--muted))"]
                          } : credits < 40 ? {
                            scale: [1, 1.02, 1]
                          } : {}}
                          transition={credits < 40 ? { duration: 2, repeat: Infinity } : { duration: 0.6 }}
                          onClick={() => navigate("/pricing")}
                        >
                          {credits < 40 ? (
                            <AlertTriangle className="h-4 w-4" />
                          ) : (
                            <Coins className={`h-4 w-4 ${credits < 80 ? '' : 'text-primary'}`} />
                          )}
                          <motion.span 
                            key={credits}
                            initial={prevCredits !== credits ? { scale: 1.5, opacity: 0 } : {}}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.3 }}
                            className="text-sm font-semibold"
                          >
                            {fmtTR(credits)}
                          </motion.span>
                          {credits < 80 && (
                            <span className="text-xs opacity-70">
                              ({Math.floor(credits / 40)} video)
                            </span>
                          )}
                        </motion.div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {credits < 40 ? (
                          <p className="text-red-500 font-medium">⚠️ Kritik! Video oluşturmak için yeterli kredi yok</p>
                        ) : credits < 80 ? (
                          <p className="text-amber-500 font-medium">⚡ Düşük kredi - {Math.floor(credits / 40)} video kaldı</p>
                        ) : (
                          <p>{fmtTR(credits)} kredi - {fmtTR(Math.floor(credits / 40))} video oluşturabilirsiniz</p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate("/pricing")}
                    className="hidden md:flex items-center gap-1"
                  >
                    <Coins className="h-4 w-4" />
                    Kredi Al
                  </Button>
                  <NotificationBell />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="relative h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 rounded-full flex-shrink-0 hidden md:flex">
                        <Avatar className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10">
                          <AvatarImage src={avatarUrl} alt={user.email || ""} />
                          <AvatarFallback className="bg-gradient-primary text-white text-xs sm:text-sm">
                            {user.email?.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56 max-h-[75vh] overflow-y-auto" align="end" forceMount>
                      <DropdownMenuLabel className="font-normal">
                        <div className="flex flex-col space-y-1">
                          <p className="text-sm font-medium leading-none">Hesabım</p>
                          <p className="text-xs leading-none text-muted-foreground">
                            {user.email}
                          </p>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <div className="sm:hidden px-2 py-1.5">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Coins className="h-4 w-4 text-primary" />
                            <span className="text-sm font-semibold">{fmtTR(credits)} Kredi</span>
                          </div>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate("/pricing")}
                          className="w-full"
                        >
                          <Coins className="h-4 w-4 mr-1" />
                          Kredi Al
                        </Button>
                      </div>
                      <DropdownMenuSeparator className="sm:hidden" />
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => navigate("/profil")}
                      >
                        Hesabım
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => navigate("/results")}
                      >
                        Stüdyom
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => navigate("/hikaye-studyosu")}
                      >
                        🎭 Hikaye Stüdyosu
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">Araçlar</DropdownMenuLabel>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/results?tool=url")}>🔗 Bağlantıdan Reklam</DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/results?tool=set")}>🖼️ Ürün Görsel Seti</DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/results?tool=arkaplan")}>🧹 Arkaplan Temizleyici</DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/results?tool=yukselt")}>⬆️ Kalite Yükseltici</DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/results?tool=deneme")}>👗 Sanal Deneme</DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/results?tool=hareket")}>🕺 Hareket Taklidi</DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/results?tool=dublaj")}>🎤 Video Dublaj</DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/results?tool=yuz")}>🎭 Yüz Değiştirme</DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/results?tool=parcala")}>🧩 Parçalara Ayırma</DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/results?tool=klon")}>🧬 Video Klonlama</DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/results?tool=afis")}>🪧 Kampanya Afişi</DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/results?tool=referans")}>🎥 Referanstan Üretim</DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/kampanya")}>🚀 Kampanya Motoru</DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/trend")}>🔥 Trend Avcısı</DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/repurpose")}>✂️ Videoyu Kliple</DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/sablonlar")}>💼 Şablon Marketplace</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => navigate("/influencers")}
                      >
                        🧑‍🎤 Influencer'larım
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer font-medium"
                        onClick={() => navigate("/davet")}
                      >
                        🎁 Davet Et & Kazan
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => navigate("/analytics")}
                      >
                        Video Analizleri
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => navigate("/payment-history")}
                      >
                        Ödeme Geçmişi
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => navigate("/integrations")}
                      >
                        Entegrasyonlar
                      </DropdownMenuItem>
                      {isAdmin && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="cursor-pointer text-primary font-semibold"
                            onClick={() => navigate("/admin")}
                          >
                            🛡️ Admin Paneli
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <NotificationToggle />
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="cursor-pointer text-destructive focus:text-destructive"
                        onClick={async () => {
                          try {
                            const { error } = await supabase.auth.signOut();
                            if (error) {
                              console.error('Logout error:', error);
                            }
                            setUser(null);
                            navigate("/");
                            window.location.reload();
                          } catch (error) {
                            console.error('Logout failed:', error);
                          }
                        }}
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        <span>Çıkış Yap</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  <Button 
                    variant="ghost"
                    onClick={() => navigate("/auth?mode=login")}
                    className="text-foreground hover:text-primary text-xs sm:text-sm px-2 sm:px-3 flex-shrink-0 hidden md:flex"
                  >
                    Giriş Yap
                  </Button>
                  <Button 
                    variant="gradient"
                    onClick={() => navigate("/auth?mode=signup")}
                    className="text-xs sm:text-sm px-2.5 sm:px-3 md:px-4 flex-shrink-0 hidden md:flex"
                  >
                    Kayıt Ol
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};
